// Event-driven approach: a simple pub-sub system
//
// This allows us to subscribe to custom events
// and trigger them from completely disconnected components.
// This way, we can have the map fire events like: clicked
// at "site A" and other components in the same page
// subscribe to that event without having to create a global
// state.

import { useCallback, useEffect, useRef } from "react";

// Event bus implementation
type Callback<T> = (message: T) => void;

// This is one variable for all the buses in the platform.
// {
//      "eventClicked": Set[
//          callback1,
//          callback2,
//      ]
// }
const channels = new Map<string, Set<Callback<any>>>();

// Returns channel, creating it if it doesn't exist.
const getChannel = (name: string) => {
    if (!channels.has(name)) {
        channels.set(name, new Set());
    }
    return channels.get(name)!;
};

// Publish event: just run all callbacks and
// pass the message along.
// If any subscribers fail, return false.
const publish = <T = unknown>(name: string, message: T) => {
    let success = true;
    for (const callback of [...getChannel(name)]) {
        try {
            callback(message);
        } catch {
            success = false;
        }
    }
    return success;
};

// Add a new callback to event bus
const subscribe = <T = unknown>(name: string, callback: Callback<T>) => {
    // Retrieves (or creates channel) and adds callback
    getChannel(name).add(callback as Callback<any>);
    return () => {
        const ch = getChannel(name);
        ch.delete(callback as Callback<any>);
    };
};

/**
 * useEventBus
 *
 * Hook to create/subscribe to an event bus in the platform.
 *
 * @param name: string -> used to name bus. Same name subscribe to the same events.
 * @param callback: (message: T) -> callback (if you want it).
 * @returns
 */

export function useEventBus<T = unknown>(name: string, callback?: Callback<T>) {
    // Store callback as a reference
    const ref = useRef(callback);

    // Update when callback changes
    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    // If name or callback changes, update
    useEffect(() => {
        // No callback, do nothing.
        if (!callback) {
            return;
        }
        // Else, register on event bus USING REF (ref is updated on previous useEffect)
        const handler: Callback<T> = (msg) => ref.current?.(msg);

        // `subscribe()` returns a unsubscribe handler!
        return subscribe(name, handler);
    }, [name, !!callback]); // eslint-disable-line react-hooks/exhaustive-deps

    return useCallback((message?: T) => publish(name, message), [name]);
}
